import type { ErrorCode } from '../api';

/** The subset of the API error codes this module produces. */
export type YouTubeErrorCode = Extract<ErrorCode, 'bad_request' | 'video_not_found' | 'transcript_unavailable' | 'internal'>;

/**
 * Finer-grained cause, e.g. for UI hints ("try client mode") or logging.
 * - invalid_id: not an 11-character video ID.
 * - not_found: deleted, private, invalid or otherwise unavailable video.
 * - no_captions: the video has no caption track at all (also: music without lyrics, most Shorts without speech).
 * - live: upcoming or running live stream (captions only exist after the stream).
 * - login_required: age-restricted, members-only or otherwise needs a signed-in YouTube account.
 * - bot_check: YouTube answered "Sign in to confirm you're not a bot" (typical for datacenter IPs).
 * - po_token_required: caption tracks exist but the timedtext endpoint returned nothing (Proof-of-Origin token gate).
 * - rate_limited: HTTP 429.
 * - network: fetch failed (offline, DNS, blocked by CORS or an extension, ...).
 * - bad_response: unexpected HTTP status or response shape.
 */
export type YouTubeErrorReason =
  | 'invalid_id'
  | 'not_found'
  | 'no_captions'
  | 'live'
  | 'login_required'
  | 'bot_check'
  | 'po_token_required'
  | 'rate_limited'
  | 'network'
  | 'bad_response';

const CODE_BY_REASON: Record<YouTubeErrorReason, YouTubeErrorCode> = {
  invalid_id: 'bad_request',
  not_found: 'video_not_found',
  no_captions: 'transcript_unavailable',
  live: 'transcript_unavailable',
  login_required: 'transcript_unavailable',
  bot_check: 'transcript_unavailable',
  po_token_required: 'transcript_unavailable',
  rate_limited: 'transcript_unavailable',
  network: 'internal',
  bad_response: 'internal',
};

/** Reasons where retrying from another IP (client mode, proxy) or later may help. */
const RETRYABLE: ReadonlySet<YouTubeErrorReason> = new Set(['bot_check', 'po_token_required', 'rate_limited', 'network', 'bad_response']);

export class YouTubeError extends Error {
  override readonly name = 'YouTubeError';
  readonly code: YouTubeErrorCode;
  readonly reason: YouTubeErrorReason;
  /** True when the same request may succeed from another IP / later (e.g. switch to client mode). */
  readonly retryable: boolean;

  constructor(reason: YouTubeErrorReason, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.reason = reason;
    this.code = CODE_BY_REASON[reason];
    this.retryable = RETRYABLE.has(reason);
  }
}

export function isYouTubeError(error: unknown): error is YouTubeError {
  return error instanceof YouTubeError;
}

export function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}
